import { Card } from '../types';
import { 
  searchExternalCards, 
  fetchBulkCards, 
  fetchBulkV2Cards, 
  mapHomuraCardToCard, 
  getCardgames 
} from '../services/supabaseService';
import {
  findGame,
  getGameSlug,
  getGameId,
  getGameName,
  isGame,
  isPokemonGame,
  detectGameFromText,
  findGameByCardNexus,
  isCardNexusGameMatch
} from '../constants/games';

export type ImportSourceType = 'cardnexus' | 'general' | 'tcgplayer';
export type CardNexusSubFormat = 'auto' | 'list' | 'csv' | 'json';

export interface ImportFormatOption {
  id: string;
  name: string;
  icon: string;
  badge?: string;
  description: string;
  placeholder: string;
  fileAccept: string;
  sampleContent: string;
}

export interface ImportSourceOption {
  id: string;
  name: string;
  icon: string;
  badge?: string;
  description: string;
  isAvailable: boolean;
  formats: ImportFormatOption[];
}

export interface ParsedListCard {
  name: string;
  code?: string;
  cardId?: string;
  setCode?: string;
  cardNum?: string;
  quantity: number;
  section: string;
  rawLine: string;
  detectedGameSlug?: string;
  detectedGameName?: string;
  condition?: string;
  language?: string;
  price?: number;
  finish?: string;
  expansionName?: string;
}

// Alias para compatibilidade
export type ParsedImportCard = ParsedListCard;

export interface ListDetectionResult {
  source: ImportSourceType;
  format: 'json' | 'csv' | 'list' | 'generic';
  detectedGame?: { slug?: string; name?: string };
}

export interface ImportListResult {
  success: boolean;
  importedItems: { card: Card; quantity: number }[];
  notFoundItems: ParsedListCard[];
  totalCards: number;
  errorMessage?: string;
}

// Exemplos oficiais fornecidos pelo CardNexus
export const CARDNEXUS_SAMPLE_JSON = `[
  {
    "totalQtyOwned": 3,
    "productId": 221077,
    "name": "Mastermind Scheme",
    "printNumber": "016",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 2.2970200000000003
  },
  {
    "totalQtyOwned": 6,
    "productId": 221102,
    "name": "Ominous Shadow",
    "printNumber": "030",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 1.87938
  },
  {
    "totalQtyOwned": 1,
    "productId": 221101,
    "name": "Cloaked Executioner",
    "printNumber": "022",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 1.513945
  },
  {
    "totalQtyOwned": 1,
    "productId": 221143,
    "name": "Shadow Resonance",
    "printNumber": "031",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 1.7749700000000002
  },
  {
    "totalQtyOwned": 2,
    "productId": 221138,
    "name": "Stifling Trap",
    "printNumber": "025",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 5.063885
  },
  {
    "totalQtyOwned": 1,
    "productId": 221140,
    "name": "Shifting Mirage",
    "printNumber": "033",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 1.096305
  },
  {
    "totalQtyOwned": 1,
    "productId": 221086,
    "name": "Grim Foreboding",
    "printNumber": "028",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 2.035995
  },
  {
    "totalQtyOwned": 2,
    "productId": 221087,
    "name": "Arrow Trap",
    "printNumber": "020",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 0.7308700000000001
  },
  {
    "totalQtyOwned": 1,
    "productId": 221123,
    "name": "Shadowstrike",
    "printNumber": "032",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 2.8190700000000004
  },
  {
    "totalQtyOwned": 2,
    "productId": 221111,
    "name": "Thieving Cut",
    "printNumber": "019",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 1.2007150000000002
  },
  {
    "totalQtyOwned": 2,
    "productId": 221092,
    "name": "Gloamspire Headhunter",
    "printNumber": "027",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 1.25292
  },
  {
    "totalQtyOwned": 1,
    "productId": 221071,
    "name": "Incapacitate",
    "printNumber": "015",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 6.16019
  },
  {
    "totalQtyOwned": 2,
    "productId": 221072,
    "name": "Haunting Demise",
    "printNumber": "029",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 0.9918950000000001
  },
  {
    "totalQtyOwned": 1,
    "productId": 221125,
    "name": "Slice and Dice",
    "printNumber": "018",
    "finish": "Standard",
    "variant": "",
    "expansion": {
      "name": "Re:Collection Shadowdancer",
      "code": "ReC-SHD",
      "slug": "recollection-shadowdancer"
    },
    "game": {
      "name": "Grand Archive TCG",
      "slug": "grand-archive"
    },
    "condition": "Near Mint",
    "language": "en",
    "location": "",
    "tags": [],
    "notes": "",
    "price": 2.035995
  }
]`;

export const CARDNEXUS_SAMPLE_CSV = `"totalQtyOwned","productId","name","printNumber","finish","variant","expansion","game","condition","language","location","tags","notes","price"
"3","221077","Mastermind Scheme","016","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","2.30"
"6","221102","Ominous Shadow","030","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","1.88"
"1","221101","Cloaked Executioner","022","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","1.51"
"1","221143","Shadow Resonance","031","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","1.77"
"2","221138","Stifling Trap","025","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","5.06"
"1","221140","Shifting Mirage","033","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","1.10"
"1","221086","Grim Foreboding","028","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","2.04"
"2","221087","Arrow Trap","020","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","0.73"
"1","221123","Shadowstrike","032","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","2.82"
"2","221111","Thieving Cut","019","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","1.20"
"2","221092","Gloamspire Headhunter","027","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","1.25"
"1","221071","Incapacitate","015","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","6.16"
"2","221072","Haunting Demise","029","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","0.99"
"1","221125","Slice and Dice","018","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","2.04"`;

export const CARDNEXUS_SAMPLE_LIST = `3 Mastermind Scheme (REC-SHD) #016
6 Ominous Shadow (REC-SHD) #030
1 Cloaked Executioner (REC-SHD) #022
1 Shadow Resonance (REC-SHD) #031
2 Stifling Trap (REC-SHD) #025
1 Shifting Mirage (REC-SHD) #033
1 Grim Foreboding (REC-SHD) #028
2 Arrow Trap (REC-SHD) #020
1 Shadowstrike (REC-SHD) #032
2 Thieving Cut (REC-SHD) #019
2 Gloamspire Headhunter (REC-SHD) #027
1 Incapacitate (REC-SHD) #015
2 Haunting Demise (REC-SHD) #029
1 Slice and Dice (REC-SHD) #018`;

export const AVAILABLE_IMPORT_SOURCES: ImportSourceOption[] = [
  {
    id: 'cardnexus',
    name: 'CardNexus',
    icon: 'fa-cubes',
    badge: 'Disponível',
    description: 'Importe coleções, decks e fichários exportados do CardNexus',
    isAvailable: true,
    formats: [
      {
        id: 'list',
        name: 'Lista',
        icon: 'fa-list-ol',
        badge: 'Texto',
        description: '3 Carta (SET) #001',
        placeholder: `Cole a lista do CardNexus aqui:\n3 Mastermind Scheme (REC-SHD) #016\n6 Ominous Shadow (REC-SHD) #030\n1 Cloaked Executioner (REC-SHD) #022\n1 Shadow Resonance (REC-SHD) #031`,
        fileAccept: '.txt',
        sampleContent: CARDNEXUS_SAMPLE_LIST
      },
      {
        id: 'csv',
        name: 'CSV',
        icon: 'fa-file-csv',
        badge: 'Planilha',
        description: 'Planilha .csv com colunas oficiais',
        placeholder: `"totalQtyOwned","productId","name","printNumber","finish","variant","expansion","game","condition","language","location","tags","notes","price"\n"3","221077","Mastermind Scheme","016","Standard","","Re:Collection Shadowdancer","Grand Archive TCG","Near Mint","en","","","","2.30"`,
        fileAccept: '.csv',
        sampleContent: CARDNEXUS_SAMPLE_CSV
      },
      {
        id: 'json',
        name: 'JSON',
        icon: 'fa-code',
        badge: 'Completo',
        description: 'Array .json com metadados e preços',
        placeholder: `[\n  {\n    "totalQtyOwned": 3,\n    "productId": 221077,\n    "name": "Mastermind Scheme",\n    "printNumber": "016",\n    "expansion": { "code": "ReC-SHD" },\n    "price": 2.29\n  }\n]`,
        fileAccept: '.json',
        sampleContent: CARDNEXUS_SAMPLE_JSON
      }
    ]
  }
];

export function getSourceById(sourceId: string): ImportSourceOption | undefined {
  return AVAILABLE_IMPORT_SOURCES.find(s => s.id === sourceId);
}

export function getFormatsForSource(sourceId: string): ImportFormatOption[] {
  const source = getSourceById(sourceId);
  return source?.formats || [];
}

/**
 * Utilitário para parsear linha de CSV respeitando aspas duplas e delimitadores internos
 */
export function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((char === ',' || char === ';' || char === '\t') && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

/**
 * Parser para JSON do CardNexus
 */
export function parseCardNexusJson(rawText: string): { cards: ParsedListCard[]; detectedGame?: { slug?: string; name?: string } } {
  try {
    const parsed = JSON.parse(rawText.trim());
    const items = Array.isArray(parsed) ? parsed : (Array.isArray(parsed.cards) ? parsed.cards : (Array.isArray(parsed.data) ? parsed.data : []));
    if (!Array.isArray(items) || items.length === 0) return { cards: [] };

    let detectedGame: { slug?: string; name?: string } | undefined = undefined;
    const cards: ParsedListCard[] = [];

    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      const name = String(item.name || item.cardName || '').trim();
      if (!name) continue;

      const quantity = Math.max(1, parseInt(String(item.totalQtyOwned ?? item.quantity ?? item.qty ?? 1), 10) || 1);
      const printNumber = item.printNumber ? String(item.printNumber).trim() : (item.number ? String(item.number).trim() : '');
      const expansionCode = item.expansion?.code 
        ? String(item.expansion.code).trim() 
        : (typeof item.expansion === 'string' ? item.expansion.trim() : (item.setCode ? String(item.setCode).trim() : ''));
      const expansionName = item.expansion?.name || item.expansionName || '';

      const itemGame = item.game?.slug || item.game?.name || item.game;
      const rowGameDef = findGameByCardNexus(itemGame) || findGame(itemGame);
      const rowGameSlug = rowGameDef?.slug || (item.game?.slug ? String(item.game.slug).toLowerCase().trim() : undefined);
      const rowGameName = rowGameDef?.name || item.game?.name;

      if (!detectedGame && rowGameSlug) {
        detectedGame = {
          slug: rowGameSlug,
          name: rowGameName
        };
      }

      let code: string | undefined = undefined;
      const pNum = printNumber.trim();
      const expCode = expansionCode.trim();

      // If printNumber already represents an alphanumeric card code (e.g. OP17-002, GD01-038, PEN340, BT1-001)
      if (pNum && !/^\d+$/.test(pNum) && /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/i.test(pNum)) {
        code = pNum;
      } else if (expCode && expCode.length <= 6 && !expCode.includes(' ') && pNum) {
        if (pNum.toUpperCase().startsWith(expCode.toUpperCase())) {
          code = pNum;
        } else {
          code = `${expCode}-${pNum}`;
        }
      } else if (pNum) {
        code = pNum;
      } else if (expCode) {
        code = expCode;
      }

      cards.push({
        name,
        code,
        setCode: expansionCode || undefined,
        cardNum: printNumber || undefined,
        quantity,
        section: 'Main',
        rawLine: `${quantity} ${name} (${expansionCode || expansionName || 'Set'}) #${printNumber || '001'}`,
        detectedGameSlug: rowGameSlug,
        detectedGameName: rowGameName,
        condition: item.condition,
        language: item.language,
        price: typeof item.price === 'number' ? item.price : undefined,
        finish: item.finish,
        expansionName
      });
    }

    return { cards, detectedGame };
  } catch {
    return { cards: [] };
  }
}

/**
 * Parser para CSV do CardNexus
 */
export function parseCardNexusCsv(rawText: string): { cards: ParsedListCard[]; detectedGame?: { slug?: string; name?: string } } {
  const lines = rawText.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return { cards: [] };

  const headerTokens = parseCsvLine(lines[0]).map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
  
  const qtyCol = headerTokens.findIndex(h => /^(totalqtyowned|quantity|qty|count)$/.test(h));
  const nameCol = headerTokens.findIndex(h => /^(name|cardname|card)$/.test(h));
  const printNumCol = headerTokens.findIndex(h => /^(printnumber|number|cardnumber|num)$/.test(h));
  const expCol = headerTokens.findIndex(h => /^(expansion|set|setname|code|edition)$/.test(h));
  const gameCol = headerTokens.findIndex(h => /^(game|tcg|gamename|cardgame|gameid|gametitle)$/.test(h));
  const condCol = headerTokens.findIndex(h => /^(condition|cond)$/.test(h));
  const langCol = headerTokens.findIndex(h => /^(language|lang)$/.test(h));
  const finishCol = headerTokens.findIndex(h => /^(finish|foil)$/.test(h));
  const priceCol = headerTokens.findIndex(h => /^(price|valor)$/.test(h));

  if (nameCol === -1) return { cards: [] };

  let detectedGame: { slug?: string; name?: string } | undefined = undefined;
  const cards: ParsedListCard[] = [];

  for (let i = 1; i < lines.length; i++) {
    const row = parseCsvLine(lines[i]);
    if (row.length === 0 || !row[nameCol]) continue;

    const name = row[nameCol].trim();
    if (!name) continue;

    const quantity = qtyCol !== -1 ? Math.max(1, parseInt(row[qtyCol], 10) || 1) : 1;
    const printNumber = printNumCol !== -1 ? row[printNumCol].trim() : '';
    const expansion = expCol !== -1 ? row[expCol].trim() : '';
    const gameName = gameCol !== -1 ? row[gameCol].trim() : '';
    const condition = condCol !== -1 ? row[condCol].trim() : '';
    const language = langCol !== -1 ? row[langCol].trim() : '';
    const finish = finishCol !== -1 ? row[finishCol].trim() : '';
    const price = priceCol !== -1 ? parseFloat(row[priceCol]) : undefined;

    const rowGameDef = findGameByCardNexus(gameName) || findGame(gameName);
    const rowGameSlug = rowGameDef?.slug || (gameName ? gameName.toLowerCase().replace(/[^a-z0-9]+/g, '-') : undefined);
    const rowGameName = rowGameDef?.name || gameName;

    if (!detectedGame && rowGameSlug) {
      detectedGame = {
        name: rowGameName,
        slug: rowGameSlug
      };
    }

    let code: string | undefined = undefined;
    const pNum = printNumber.trim();
    const exp = expansion.trim();

    // If printNumber is already an alphanumeric card code (e.g. OP17-002, GD01-038, PEN340, BT1-001)
    if (pNum && !/^\d+$/.test(pNum) && /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/i.test(pNum)) {
      code = pNum;
    } else if (exp && exp.length <= 6 && !exp.includes(' ') && pNum) {
      if (pNum.toUpperCase().startsWith(exp.toUpperCase())) {
        code = pNum;
      } else {
        code = `${exp}-${pNum}`;
      }
    } else if (pNum) {
      code = pNum;
    } else if (exp) {
      code = exp;
    }

    cards.push({
      name,
      code,
      setCode: expansion || undefined,
      cardNum: printNumber || undefined,
      quantity,
      section: 'Main',
      rawLine: lines[i],
      detectedGameSlug: rowGameSlug,
      detectedGameName: rowGameName,
      condition,
      language,
      finish,
      price: !isNaN(price as number) ? price : undefined,
      expansionName: expansion
    });
  }

  return { cards, detectedGame };
}

/**
 * Parser para Lista textual do CardNexus
 * Ex: "3 Mastermind Scheme (REC-SHD) #016"
 */
export function parseCardNexusList(rawText: string): { cards: ParsedListCard[]; detectedGame?: { slug?: string; name?: string } } {
  const lines = rawText.split('\n');
  const cards: ParsedListCard[] = [];
  const detected = detectGameFromText(rawText);
  const detectedGame = detected ? { slug: detected.slug, name: detected.name } : undefined;

  for (const rawLine of lines) {
    let line = rawLine.trim();
    if (!line) continue;
    if (line.startsWith('//') || line.startsWith('#') || line.startsWith('/*')) continue;

    let finish: string | undefined = undefined;
    // Check for trailing finish / foil / condition markers, e.g. *F*, *Foil*, *Reverse*, [Foil], (Foil), *H*, *R*, etc.
    const finishMatch = line.match(/\s+([*\[(](?:F|Foil|Holo|Reverse|Reverse Holo|H|R|1st)[*\])]|\*[A-Za-z0-9\-_]+\*)\s*$/i);
    if (finishMatch) {
      finish = finishMatch[1].replace(/[*\[\]()]/g, '').trim();
      line = line.slice(0, finishMatch.index).trim();
    }

    const nexusMatch = line.match(/^(\d+)\s*[xX\*]?\s+(.+?)\s+\(([^)]+)\)(?:\s*(?:#|no\.|num\.)?\s*([A-Za-z0-9\-_\/]+))?$/i);
    if (nexusMatch) {
      const quantity = Math.max(1, parseInt(nexusMatch[1], 10) || 1);
      const name = nexusMatch[2].trim();
      const setCode = nexusMatch[3].trim();
      const cardNum = nexusMatch[4] ? nexusMatch[4].trim() : undefined;
      const code = cardNum ? `${setCode}-${cardNum}` : setCode;

      cards.push({
        name,
        code,
        setCode,
        cardNum,
        quantity,
        section: 'Main',
        rawLine,
        finish,
        detectedGameSlug: detected?.slug,
        detectedGameName: detected?.name
      });
      continue;
    }

    // Suporte a formato PTCGL em modo lista (ex: "4 Charmander MEW 4" ou "1 Charizard ex OBF 125")
    const ptcglMatch = line.match(/^(\d+)\s*[xX\*]?\s+(.+?)\s+([A-Za-z0-9]{2,6})\s+(?:#|no\.)?([0-9]{1,4}[a-zA-Z0-9]*)$/i);
    if (ptcglMatch) {
      const quantity = Math.max(1, parseInt(ptcglMatch[1], 10) || 1);
      const name = ptcglMatch[2].trim();
      const setCode = ptcglMatch[3].trim();
      const cardNum = ptcglMatch[4].trim();
      const code = `${setCode}-${cardNum}`;

      cards.push({
        name,
        code,
        setCode,
        cardNum,
        quantity,
        section: 'Main',
        rawLine,
        finish,
        detectedGameSlug: detected?.slug || 'pokemon',
        detectedGameName: detected?.name || 'Pokémon'
      });
    }
  }

  return { cards, detectedGame };
}

/**
 * Detecta a fonte e formato mais provável do conteúdo
 */
export function detectListFormatAndSource(rawText: string): ListDetectionResult {
  const trimmed = rawText.trim();
  if (!trimmed) {
    return { source: 'general', format: 'generic' };
  }

  if (trimmed.startsWith('[') || (trimmed.startsWith('{') && (trimmed.includes('"cards"') || trimmed.includes('"data"')))) {
    if (trimmed.includes('totalQtyOwned') || trimmed.includes('printNumber') || trimmed.includes('productId')) {
      const res = parseCardNexusJson(trimmed);
      return { source: 'cardnexus', format: 'json', detectedGame: res.detectedGame };
    }
    return { source: 'general', format: 'json' };
  }

  if (trimmed.includes('totalQtyOwned') || (trimmed.includes('productId') && trimmed.includes('printNumber'))) {
    const res = parseCardNexusCsv(trimmed);
    return { source: 'cardnexus', format: 'csv', detectedGame: res.detectedGame };
  }

  const lines = trimmed.split('\n').map(l => l.trim()).filter(Boolean);
  const detectedFromText = detectGameFromText(trimmed);
  const detectedGame = detectedFromText ? { name: detectedFromText.name, slug: detectedFromText.slug } : undefined;

  const cardNexusListMatches = lines.filter(l => /^(\d+)\s*[xX\*]?\s+.+?\s+\([^)]+\)\s*#\d+/i.test(l));
  if (cardNexusListMatches.length > 0 && cardNexusListMatches.length >= Math.ceil(lines.length * 0.4)) {
    return { source: 'cardnexus', format: 'list', detectedGame };
  }

  if (trimmed.includes('[TCGPlayer]') || lines.some(l => /^\d+\s+.+?\s+\[[A-Za-z0-9]+\]/.test(l))) {
    return { source: 'tcgplayer', format: 'list', detectedGame };
  }

  return { source: 'general', format: 'generic', detectedGame };
}

/**
 * Analisador unificado de listas de cartas (para pastas, coleções e fichários)
 */
export function parseFolderCardList(
  rawText: string, 
  options?: { source?: ImportSourceType; format?: CardNexusSubFormat }
): ParsedListCard[] {
  if (!rawText || !rawText.trim()) return [];

  const trimmed = rawText.trim();
  const source = options?.source || 'cardnexus';
  const format = options?.format || 'auto';

  // 1. CardNexus
  if (source === 'cardnexus') {
    if (format === 'json' || (format === 'auto' && (trimmed.startsWith('[') || trimmed.startsWith('{')))) {
      const res = parseCardNexusJson(trimmed);
      if (res.cards.length > 0) return res.cards;
    }
    if (format === 'csv' || (format === 'auto' && (trimmed.includes('totalQtyOwned') || trimmed.includes('productId')))) {
      const res = parseCardNexusCsv(trimmed);
      if (res.cards.length > 0) return res.cards;
    }
    if (format === 'list' || format === 'auto') {
      const res = parseCardNexusList(trimmed);
      if (res.cards.length > 0) return res.cards;
    }
  }

  // 2. Suporte a array JSON genérico de códigos
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const arr = JSON.parse(trimmed);
      if (Array.isArray(arr)) {
        const counts: Record<string, number> = {};
        for (const item of arr) {
          if (typeof item === 'string' && !item.toLowerCase().includes('export')) {
            const clean = item.trim();
            if (clean) counts[clean] = (counts[clean] || 0) + 1;
          }
        }
        if (Object.keys(counts).length > 0) {
          return Object.entries(counts).map(([code, quantity]) => ({
            name: code,
            code: code,
            quantity,
            section: 'Main',
            rawLine: `${quantity}x ${code}`
          }));
        }
      }
    } catch {
      // Ignora erro
    }
  }

  // 3. Fallback linha por linha (Texto Livre, Simuladores, CSVs simples e Listas)
  const lines = rawText.split('\n');
  const parsedCards: ParsedListCard[] = [];

  for (const rawLine of lines) {
    let line = rawLine.trim();
    if (!line) continue;

    if (line.startsWith('//') || line.startsWith('#') || line.startsWith('/*')) continue;
    if (/^(pokemon|treinador|energia|trainer|energy|deck|leader|character|event|stage|sideboard|side|extra|main|material|eggs|base|commander):?$/i.test(line)) continue;
    if (/^[A-Za-z\s]+:\s*\d+$/i.test(line)) continue;

    line = line.replace(/^[\*\-\•]\s+/, '').replace(/^\d+\.\s+/, '');

    let finish: string | undefined = undefined;
    const finishMatch = line.match(/\s+([*\[(](?:F|Foil|Holo|Reverse|Reverse Holo|H|R|1st)[*\])]|\*[A-Za-z0-9\-_]+\*)\s*$/i);
    if (finishMatch) {
      finish = finishMatch[1].replace(/[*\[\]()]/g, '').trim();
      line = line.slice(0, finishMatch.index).trim();
    }

    const nexusMatch = line.match(/^(\d+)\s*[xX\*]?\s+(.+?)\s+\(([^)]+)\)(?:\s*(?:#|no\.|num\.)?\s*([A-Za-z0-9\-_\/]+))?$/i);
    if (nexusMatch) {
      const quantity = Math.max(1, parseInt(nexusMatch[1], 10) || 1);
      const name = nexusMatch[2].trim();
      const setCode = nexusMatch[3].trim();
      const cardNum = nexusMatch[4] ? nexusMatch[4].trim() : undefined;
      const code = cardNum ? `${setCode}-${cardNum}` : setCode;

      parsedCards.push({
        name,
        code,
        setCode,
        cardNum,
        quantity,
        section: 'Main',
        rawLine,
        finish
      });
      continue;
    }

    // Suporte a formato PTCGL em modo lista (ex: "4 Charmander MEW 4" ou "1 Charizard ex OBF 125")
    const ptcglMatch = line.match(/^(\d+)\s*[xX\*]?\s+(.+?)\s+([A-Za-z0-9]{2,6})\s+(?:#|no\.)?([0-9]{1,4}[a-zA-Z0-9]*)$/i);
    if (ptcglMatch) {
      const quantity = Math.max(1, parseInt(ptcglMatch[1], 10) || 1);
      const name = ptcglMatch[2].trim();
      const setCode = ptcglMatch[3].trim();
      const cardNum = ptcglMatch[4].trim();
      const code = `${setCode}-${cardNum}`;

      parsedCards.push({
        name,
        code,
        setCode,
        cardNum,
        quantity,
        section: 'Main',
        rawLine,
        finish
      });
      continue;
    }

    let quantity = 1;
    let cardIdentifier = line;

    if (line.includes(',') || line.includes(';') || line.includes('\t')) {
      const delim = line.includes('\t') ? '\t' : (line.includes(';') ? ';' : ',');
      const parts = line.split(delim).map(p => p.trim().replace(/^"|"$/g, '')).filter(Boolean);
      if (parts.length >= 2) {
        if (/^\d+$/.test(parts[0])) {
          quantity = parseInt(parts[0], 10);
          cardIdentifier = parts.slice(1).join(' ');
        } else if (/^\d+$/.test(parts[1])) {
          quantity = parseInt(parts[1], 10);
          cardIdentifier = [parts[0], ...parts.slice(2)].join(' ');
        } else if (/^\d+$/.test(parts[parts.length - 1])) {
          quantity = parseInt(parts[parts.length - 1], 10);
          cardIdentifier = parts.slice(0, -1).join(' ');
        }
      }
    } else {
      const qtyMatch = cardIdentifier.match(/^(\d+)\s*[xX\*]?\s+(.+)$/);
      if (qtyMatch) {
        quantity = parseInt(qtyMatch[1], 10);
        cardIdentifier = qtyMatch[2].trim();
      } else {
        const compactMatch = cardIdentifier.match(/^(\d+)x([A-Za-z0-9\-_]+)$/);
        if (compactMatch) {
          quantity = parseInt(compactMatch[1], 10);
          cardIdentifier = compactMatch[2].trim();
        }
      }
    }

    let code: string | undefined = undefined;
    let name = cardIdentifier;

    const parenMatch = cardIdentifier.match(/^(.*?)\s*\(([A-Za-z0-9\-_\s]+)\)$/);
    if (parenMatch) {
      name = parenMatch[1].trim();
      code = parenMatch[2].trim();
    } else {
      const codeRegex = /\b([A-Za-z0-9]{2,6}-[0-9]{2,4}[a-zA-Z0-9_]*)\b/;
      const codeMatch = cardIdentifier.match(codeRegex);
      if (codeMatch) {
        code = codeMatch[1];
        if (cardIdentifier === code) {
          name = code;
        } else {
          name = cardIdentifier.replace(code, '').trim() || code;
        }
      } else {
        const ptcglMatch = cardIdentifier.match(/^(.*?)\s+([A-Za-z]{2,5})\s+(\d{1,4})$/);
        if (ptcglMatch) {
          name = ptcglMatch[1].trim();
          code = `${ptcglMatch[2]} ${ptcglMatch[3]}`;
        }
      }
    }

    parsedCards.push({
      name: name || cardIdentifier,
      code,
      quantity: Math.max(1, quantity),
      section: 'Main',
      rawLine,
      finish
    });
  }

  return parsedCards;
}

function buildSetRegex(str: string): string {
  return str.split(/[-_\s]+/).filter(Boolean).map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*');
}

function cleanCardName(str: string): string {
  return (str || '')
    .replace(/\s*\(\s*null\s*\)/gi, '')
    .replace(/\s*\[\s*null\s*\]/gi, '')
    .replace(/\s*\(\s*undefined\s*\)/gi, '')
    .replace(/\s*\[\s*undefined\s*\]/gi, '')
    .replace(/\s*\(\s*\d+\s*\)$/g, '')
    .replace(/\s*\[\s*\d+\s*\]$/g, '')
    .replace(/\([^)]+\)/g, '')
    .replace(/\[[^\]]+\]/g, '')
    .trim();
}

/**
 * Executa a busca e validação de cartas para listas e pastas.
 * Suporta tanto um jogo específico quanto múltiplos jogos simultâneos (gameSlug='all').
 */
export async function executeFetchValidatedListCards(
  gameSlug: string,
  cardsToImport: ParsedListCard[],
  onProgress?: (current: number, total: number, cardName: string) => void
): Promise<ImportListResult> {
  const total = cardsToImport.length;
  if (total === 0) {
    return {
      success: true,
      importedItems: [],
      notFoundItems: [],
      totalCards: 0
    };
  }

  // Se o usuário solicitou validar "todos os jogos" e a lista contém múltiplos jogos
  const distinctGames = Array.from(new Set(
    cardsToImport.map(c => c.detectedGameSlug ? getGameSlug(c.detectedGameSlug) : (gameSlug !== 'all' ? getGameSlug(gameSlug) : 'one-piece'))
  ));

  if (gameSlug === 'all' && distinctGames.length > 1) {
    const allResolved: { card: Card; quantity: number }[] = [];
    const allMissing: ParsedListCard[] = [];
    let processedTotal = 0;

    for (const g of distinctGames) {
      const subCards = cardsToImport.filter(c => {
        const cGame = c.detectedGameSlug ? getGameSlug(c.detectedGameSlug) : 'one-piece';
        return cGame === g;
      });

      const subRes = await executeFetchValidatedListCardsForSingleGame(
        g,
        subCards,
        (curr, _subTotal, name) => {
          if (onProgress) {
            onProgress(processedTotal + curr, total, `[${getGameName(g)}] ${name}`);
          }
        }
      );

      processedTotal += subCards.length;
      allResolved.push(...subRes.importedItems);
      allMissing.push(...subRes.notFoundItems);
    }

    const totalCards = allResolved.reduce((acc, curr) => acc + curr.quantity, 0);
    return {
      success: allResolved.length > 0,
      importedItems: allResolved,
      notFoundItems: allMissing,
      totalCards,
      errorMessage: allResolved.length === 0 ? 'Nenhuma carta foi encontrada no catálogo para os jogos da lista.' : undefined
    };
  }

  const effectiveGame = gameSlug === 'all' ? (distinctGames[0] || 'one-piece') : gameSlug;
  return executeFetchValidatedListCardsForSingleGame(effectiveGame, cardsToImport, onProgress);
}

async function executeFetchValidatedListCardsForSingleGame(
  gameSlug: string,
  cardsToImport: ParsedListCard[],
  onProgress?: (current: number, total: number, cardName: string) => void
): Promise<ImportListResult> {
  const total = cardsToImport.length;
  if (total === 0) {
    return {
      success: true,
      importedItems: [],
      notFoundItems: [],
      totalCards: 0
    };
  }

  try {
    const cardGames = await getCardgames();
    const gameDef = findGame(gameSlug);
    const gameData = (cardGames as any[])?.find((g: any) => 
      isGame(g.id || g.slug, gameSlug)
    ) || gameDef;

    const targetSlug = gameDef?.slug || getGameSlug(gameSlug);
    const targetGameId = gameData?.id || gameDef?.id || getGameId(gameSlug, 1);
    const targetGameName = gameData?.name || gameDef?.name || getGameName(gameSlug);
    const resolvedItems: { card: Card; quantity: number }[] = [];
    const missingCards: ParsedListCard[] = [];

    const isKyubey = (gameData?.sauce || gameDef?.sauce) === 'kyubey';
    const isPokemon = isPokemonGame(targetSlug);
    const foundCardsMap = new Map<string, Card>();

    const cleanCode = (c?: string) => c ? c.replace(/\s+/g, '').replace(/[*#]/g, '').trim() : '';

    const registerCard = (cData: any, queryKey?: string) => {
      if (!cData) return null;
      const cardObj = mapHomuraCardToCard(cData, targetGameName, targetGameId);
      if (!cardObj) return null;

      if (cData.set) {
        if (!cardObj.set_name) cardObj.set_name = cData.set.name || cData.set.beauty_name;
        if (!cardObj.set_code) cardObj.set_code = cData.set.set_code;
      }

      if (queryKey) {
        foundCardsMap.set(queryKey.toUpperCase(), cardObj);
        foundCardsMap.set(queryKey.toLowerCase(), cardObj);
        foundCardsMap.set(queryKey.replace(/[^A-Za-z0-9]/g, '').toUpperCase(), cardObj);
        foundCardsMap.set(`name:${cleanCardName(queryKey).toLowerCase()}`, cardObj);
      }
      if (cardObj.id) {
        foundCardsMap.set(String(cardObj.id).trim().toUpperCase(), cardObj);
        foundCardsMap.set(String(cardObj.id).trim().toLowerCase(), cardObj);
      }
      if (cardObj.code) {
        foundCardsMap.set(cardObj.code.trim().toUpperCase(), cardObj);
        foundCardsMap.set(cardObj.code.replace(/[^A-Za-z0-9]/g, '').toUpperCase(), cardObj);
      }
      if (cardObj.name) {
        foundCardsMap.set(`name:${cardObj.name.trim().toLowerCase()}`, cardObj);
        foundCardsMap.set(`name:${cleanCardName(cardObj.name).toLowerCase()}`, cardObj);
      }
      return cardObj;
    };

    // 1. Tratamento especializado para Pokémon (Bulk V2 por set e número de coleção)
    if (isPokemon) {
      const pokemonItems = cardsToImport.filter(c => c.cardNum || (c.code && /\d+/.test(c.code)));
      if (pokemonItems.length > 0) {
        if (onProgress) onProgress(1, total, `Consultando catálogo Pokémon (${pokemonItems.length} cartas)...`);

        // A) Itens com setCode que parece nome de coleção (ex: PLASMA-BLAST, DARK-EXPLORERS, SM-TEAM-UP)
        const nameSetQueries = pokemonItems.filter(c => c.setCode && (c.setCode.includes('-') || c.setCode.includes(' ') || c.setCode.length > 4));
        if (nameSetQueries.length > 0) {
          try {
            const queries = nameSetQueries.map(c => {
              const num = (c.cardNum || c.code || '').replace(/[^0-9]/g, '').replace(/^0+/, '') || '1';
              return {
                'set.name': { regex: buildSetRegex(c.setCode!), options: 'i' },
                code: { regex: `^0*${num}/` }
              };
            });
            const v2Res = await fetchBulkV2Cards('pokemon', queries);
            if (v2Res && Array.isArray(v2Res.data)) {
              v2Res.data.forEach((entry, idx) => {
                if (entry.data) {
                  const item = nameSetQueries[idx];
                  const qKey = `${item.setCode}-${item.cardNum}`;
                  registerCard(entry.data, qKey);
                  if (item.code) registerCard(entry.data, item.code);
                  registerCard(entry.data, `pokemon:${item.name.toLowerCase()}:${item.cardNum}`);
                }
              });
            }
          } catch (e) {
            console.warn('Erro ao consultar Bulk V2 Pokémon com set.name:', e);
          }
        }

        // B) Itens com setCode abreviado (ex: PLB, DEX, MEW, OBF, SVI, SM9)
        const codeSetQueries = pokemonItems.filter(c => {
          const qKey = `${c.setCode}-${c.cardNum}`;
          const isResolved = foundCardsMap.has(qKey.toUpperCase()) || (c.code && foundCardsMap.has(c.code.toUpperCase()));
          return !isResolved && c.setCode && !c.setCode.includes('-') && c.setCode.length <= 6;
        });

        if (codeSetQueries.length > 0) {
          try {
            const queries = codeSetQueries.map(c => {
              const num = (c.cardNum || c.code || '').replace(/[^0-9]/g, '').replace(/^0+/, '') || '1';
              return {
                'set.set_code': c.setCode!,
                code: { regex: `^0*${num}/` }
              };
            });
            const v2Res = await fetchBulkV2Cards('pokemon', queries);
            if (v2Res && Array.isArray(v2Res.data)) {
              v2Res.data.forEach((entry, idx) => {
                if (entry.data) {
                  const item = codeSetQueries[idx];
                  const qKey = `${item.setCode}-${item.cardNum}`;
                  registerCard(entry.data, qKey);
                  if (item.code) registerCard(entry.data, item.code);
                  registerCard(entry.data, `pokemon:${item.name.toLowerCase()}:${item.cardNum}`);
                }
              });
            }
          } catch (e) {
            console.warn('Erro ao consultar Bulk V2 Pokémon com set.set_code:', e);
          }
        }

        // C) Itens Pokémon ainda não resolvidos: busca pelo nome da carta + número
        const nameAndNumQueries = pokemonItems.filter(c => {
          const qKey = `${c.setCode}-${c.cardNum}`;
          const isResolved = foundCardsMap.has(qKey.toUpperCase()) || (c.code && foundCardsMap.has(c.code.toUpperCase())) || foundCardsMap.has(`pokemon:${c.name.toLowerCase()}:${c.cardNum}`);
          return !isResolved && c.name && c.cardNum;
        });

        if (nameAndNumQueries.length > 0) {
          try {
            const queries = nameAndNumQueries.map(c => {
              const num = c.cardNum!.replace(/[^0-9]/g, '').replace(/^0+/, '') || '1';
              return {
                name: { regex: `^${cleanCardName(c.name)}`, options: 'i' },
                code: { regex: `^0*${num}/` }
              };
            });
            const v2Res = await fetchBulkV2Cards('pokemon', queries);
            if (v2Res && Array.isArray(v2Res.data)) {
              v2Res.data.forEach((entry, idx) => {
                if (entry.data) {
                  const item = nameAndNumQueries[idx];
                  const qKey = `${item.setCode}-${item.cardNum}`;
                  registerCard(entry.data, qKey);
                  if (item.code) registerCard(entry.data, item.code);
                  registerCard(entry.data, `pokemon:${item.name.toLowerCase()}:${item.cardNum}`);
                }
              });
            }
          } catch (e) {
            console.warn('Erro ao consultar Bulk V2 Pokémon por nome+número:', e);
          }
        }
      }
    }

    // 2. Busca padrão em lote por código se não for Kyubey
    if (!isKyubey) {
      const codeList = Array.from(new Set(
        cardsToImport.map(c => cleanCode(c.code)).filter(c => c && !foundCardsMap.has(c.toUpperCase()))
      ));

      if (codeList.length > 0) {
        if (onProgress) onProgress(Math.floor(total * 0.3), total, `Buscando ${codeList.length} cartas por código...`);
        try {
          const bulkCodeResult = await fetchBulkCards(gameSlug, 'code', codeList);
          if (bulkCodeResult && Array.isArray(bulkCodeResult.data)) {
            for (const entry of bulkCodeResult.data) {
              if (entry.data) {
                registerCard(entry.data, entry.query);
              }
            }
          }
        } catch (e) {
          console.warn('Erro na busca em lote por código:', e);
        }
      }

      // Busca por nome das que ainda não foram encontradas
      const nameList = Array.from(new Set(
        cardsToImport
          .filter(c => {
            const cCode = cleanCode(c.code);
            const qKey = `${c.setCode}-${c.cardNum}`;
            const isResolved = (cCode && foundCardsMap.has(cCode.toUpperCase())) ||
                               foundCardsMap.has(qKey.toUpperCase()) ||
                               foundCardsMap.has(`pokemon:${c.name.toLowerCase()}:${c.cardNum}`);
            return !isResolved;
          })
          .map(c => cleanCardName(c.name))
          .filter(Boolean)
      ));

      if (nameList.length > 0) {
        if (onProgress) onProgress(Math.floor(total * 0.5), total, `Buscando ${nameList.length} cartas por nome...`);
        try {
          const bulkNameResult = await fetchBulkCards(gameSlug, 'name', nameList);
          if (bulkNameResult && Array.isArray(bulkNameResult.data)) {
            for (const entry of bulkNameResult.data) {
              if (entry.data) {
                registerCard(entry.data, entry.query);
              }
            }
          }
        } catch (e) {
          console.warn('Erro na busca em lote por nome:', e);
        }
      }
    }

    // 3. Mapeia itens encontrados e separa faltantes
    let processed = 0;
    for (const item of cardsToImport) {
      processed++;
      const c = cleanCode(item.code);
      const n = (item.name || '').trim().toLowerCase();
      const cleanN = cleanCardName(item.name).toLowerCase();
      const qKey = `${item.setCode}-${item.cardNum}`.toUpperCase();

      let found = (c && foundCardsMap.get(c.toUpperCase())) || 
                  (c && foundCardsMap.get(c.replace(/[^A-Za-z0-9]/g, '').toUpperCase())) ||
                  foundCardsMap.get(qKey) ||
                  foundCardsMap.get(`pokemon:${n}:${item.cardNum}`) ||
                  foundCardsMap.get(`name:${cleanN}`) || 
                  foundCardsMap.get(`name:${n}`) || 
                  undefined;

      if (found) {
        const existingIdx = resolvedItems.findIndex(
          i => i.card.id === found!.id || (i.card.code && found!.code && i.card.code === found!.code)
        );

        // Clona e anexa finish se houver
        const cardToUse = item.finish ? { ...found, finish: item.finish } : found;

        if (existingIdx >= 0) {
          resolvedItems[existingIdx].quantity += item.quantity;
        } else {
          resolvedItems.push({ card: cardToUse, quantity: item.quantity });
        }
      } else {
        missingCards.push(item);
      }

      if (onProgress) onProgress(processed, total, item.name || item.code || '');
    }

    // 4. Fallback inteligente para cartas ainda faltantes
    if (missingCards.length > 0) {
      for (const item of missingCards) {
        let foundCard: any = null;
        try {
          const query = cleanCardName(item.name) || item.code;
          if (query) {
            const res = await searchExternalCards(gameSlug, query, 1, 15);
            if (res?.data && res.data.length > 0) {
              const normTargetName = cleanCardName(item.name).toLowerCase().trim();
              const targetNum = item.cardNum ? item.cardNum.replace(/[^0-9]/g, '') : '';

              // Se tiver número de coleção (ex: Pokemon #14, #1, #11)
              if (targetNum) {
                foundCard = res.data.find((c: any) => {
                  const cardCode = String(c.code || c.number || '');
                  return cardCode.startsWith(`${targetNum}/`) || cardCode === targetNum || cardCode.endsWith(`/${targetNum}`);
                });
              }

              if (!foundCard) {
                foundCard = res.data.find((c: any) => cleanCardName(c.name || '').toLowerCase().trim() === normTargetName) ||
                            res.data.find((c: any) => String(c.code || '').toLowerCase() === (item.code || '').toLowerCase()) ||
                            res.data[0];
              }
            }
          }
        } catch {
          // Ignora erro de requisição fallback
        }

        if (foundCard) {
          const cardWithFinish = item.finish ? { ...foundCard, finish: item.finish } : foundCard;
          resolvedItems.push({ card: cardWithFinish, quantity: item.quantity });
        } else {
          // Se tiver metadados completos (ex: CardNexus), preserva a carta com os dados existentes
          if (item.setCode || item.cardNum || item.price !== undefined || item.detectedGameSlug) {
            const fallbackCard: any = {
              id: item.code || `${gameSlug}-${item.name.replace(/\s+/g, '-').toLowerCase()}-${item.cardNum || '0'}`,
              code: item.code || item.cardNum || '',
              name: item.name,
              set_code: item.setCode || item.expansionName || '',
              collector_number: item.cardNum || '',
              game_id: targetGameId,
              game: gameData?.name || 'Card Game',
              price: item.price ? Number((item.price * 5).toFixed(2)) : undefined,
              rarity: item.finish || 'Standard',
              finish: item.finish,
              image_url: undefined
            };
            resolvedItems.push({ card: fallbackCard, quantity: item.quantity });
          }
        }
      }
    }

    const totalCards = resolvedItems.reduce((acc, curr) => acc + curr.quantity, 0);

    return {
      success: resolvedItems.length > 0,
      importedItems: resolvedItems,
      notFoundItems: missingCards.filter(m => !resolvedItems.some(r => r.card.name === m.name || (r.card.code && r.card.code === m.code))),
      totalCards,
      errorMessage: resolvedItems.length === 0 ? 'Nenhuma carta foi encontrada no catálogo para o jogo selecionado.' : undefined
    };
  } catch (err: any) {
    console.error('Erro na validação de lista:', err);
    return {
      success: false,
      importedItems: [],
      notFoundItems: cardsToImport,
      totalCards: 0,
      errorMessage: err.message || 'Erro inesperado ao consultar cartas da lista.'
    };
  }
}
